import {
  Box,
  Grid,
  ThemeProvider,
  Typography,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { useEffect, useState } from "react";
import { customTheme } from "../../components/customTheme";
import "../../css/globalVariables.css";
import upArrow from "../../img/upArrow.svg";
import AdminPortalHeader from "../../components/AdminHeader/AdminPortalHeader";
import { DashboardCard } from "../../components/AdminDashboard/DashboardCard";
import DashboardCircleChart from "../../components/AdminDashboard/DashboardCircleChart";
import { DashboardChart } from "../../components/AdminDashboard/DashboardChart";
import DashboardBarChart from "../../components/AdminDashboard/DashboardBarChart";
import {
  getAbrechenbareMitgliederForCurrentMonth,
  getAktiveAnmeldungenForCurrentMonth,
  getBottomTenAnwesenheit,
  getBottomTenAuslastung,
  getKuendigungenForTwoMonths,
  getStandortAuslastung,
  getHistorischeWerte,
  getMahnwesenDashboardValues,
  getMahngebuhrenDashboard,
  getAnwesenheitAuslastung,
  getBuchungsAuslastung,
  getEintritteAustritteData,
} from "../../api/admin-dashboard-api";
import {
  AbrechenbareMitglieder,
  AktiveAnmeldungen,
  AnwesenheitAuslastung,
  BuchungsAuslastung,
  Kuendigung,
  StandortAuslastung,
} from "../../models/admin-dashboard-entities";
import { ChartsLegend } from "../../components/AdminDashboard/ChartsLegend";
import { DashboardDialog } from "../../components/AdminDashboard/DashboardDialog";
import LocationDashboardChart from "../../components/AdminDashboard/LocationDashboardChart";
import MahnwesenBarChart from "../../components/AdminDashboard/MahnwesenBarChart";
import { DashboardDialogFilter } from "../../components/AdminDashboard/DashboardDialogFilter";
import { BottomFilter } from "../../components/AdminDashboard/BottomFilter";
import { BottomNoData } from "../../components/AdminDashboard/BottomNoData";

function roundUpToNearest500(value: number): number {
  return Math.ceil(value / 500) * 500;
}

function roundUpToNearest50(value: number): number {
  return Math.ceil(value / 50) * 50;
}

const monthTitleStyle = {
  color: "#E1BB41",
  fontSize: "16px",
  textAlign: "left",
  fontWeight: "bold",
};

const bigNumberStyle = {
  color: "#607783",
  fontSize: "64px",
  textAlign: "left",
};

const AdminDashboardPage: React.FC = () => {
  const LIMIT_FOR_MAHNGEBUHR = 6;

  const theme = useTheme();
  const isMobileView = useMediaQuery(theme.breakpoints.down("sm"));

  const [aktiveAnmeldungen, setAktiveAnmeldungen] =
    useState<AktiveAnmeldungen>(undefined);
  const [abrechenbareMitglieder, setAbrechenbareMitglieder] =
    useState<AbrechenbareMitglieder>(undefined);
  const [bottomTenAuslastung, setBottomTenAuslastung] =
    useState<Record<string, BuchungsAuslastung>>(undefined);
  const [allAuslastung, setAllAuslastung] =
    useState<Record<string, BuchungsAuslastung>>(undefined);
  const [bottomTenAnwesenheit, setBottomTenAnwesenheit] =
    useState<Record<string, AnwesenheitAuslastung>>(undefined);
  const [allAnwesenheit, setAllAnwesenheit] =
    useState<Record<string, AnwesenheitAuslastung>>(undefined);
  interface DashboardAustritteEintritte {
    kundigungen: number[];
    gruppeVerlassen: number[];
    austritte: number[];
    neueintritte: number[];
    sportgruppeHinzufugen: number[];
    eintritte: number[];
    labels: string[];
  }
  const [dashboardAustritteEintritte, setDashboardAustritteEintritte] =
    useState<DashboardAustritteEintritte>(undefined);

  const [kuendigungen, setKuendigungen] = useState<Kuendigung>(undefined);
  const [anwesenheitAuslastung, setAnwesenheitAuslastung] =
    useState<AnwesenheitAuslastung>(undefined);
  const [buchungsAuslastung, setBuchungsAuslastung] =
    useState<BuchungsAuslastung>(undefined);
  const [anwesenheitHistorie, setAnwesenheitHistorie] = useState<number[]>([]);
  const [auslastungHistorie, setAuslastungHistorie] = useState<number[]>([]);
  const [standortAuslastung, setStandortAuslastung] =
    useState<StandortAuslastung>(undefined);
  const [mitgliederHistorie, setMitgliederHistorie] = useState<number[]>([]);
  const [isAuslastungDialogOpen, setIsAuslastungDialogOpen] = useState(false);
  const [isAnwesenheitDialogOpen, setIsAnwesenheitDialogOpen] = useState(false);

  /* Bottom Filter */
  interface BottomFilterType {
    startDate: Date;
    endDate: Date;
  }

  const [bottomAnwesenheitFilter, setBottomAnwesenheitFilter] =
    useState<BottomFilterType>({ startDate: null, endDate: null });

  useEffect(() => {
    if (
      !bottomAnwesenheitFilter.startDate ||
      !bottomAnwesenheitFilter.endDate
    ) {
      return;
    }
    setLoadingStatus((prevState) => ({
      ...prevState,
      anwesenheit: true,
      anwesenheitAuslastung: true,
      anwesenheitDialog: true,
    }));
    getBottomTenAnwesenheit(
      bottomAnwesenheitFilter.startDate,
      bottomAnwesenheitFilter.endDate
    ).then((response) => {
      setBottomTenAnwesenheit(response.bottomTen);
      setAllAnwesenheit(response.all);
      setLoadingStatus((prevState) => ({
        ...prevState,
        anwesenheit: false,
        anwesenheitDialog: false,
      }));
    });
    getAnwesenheitAuslastung(
      bottomAnwesenheitFilter.startDate,
      bottomAnwesenheitFilter.endDate
    ).then((response) => {
      setAnwesenheitAuslastung(response);
      setLoadingStatus((prevState) => ({
        ...prevState,
        anwesenheitAuslastung: false,
      }));
    });
  }, [bottomAnwesenheitFilter]);

  /* Mahnwesen */
  const [importMahnungenCount, setImportMahnungenCount] = useState<number>(0);
  const [expiredMahnungenCount, setExpiredMahnungenCount] = useState<number>(0);
  const [mahngebuhren, setMahngebuhren] = useState<Record<string, number>>();

  const [loadingStatus, setLoadingStatus] = useState({
    aktiveAnmeldungen: true,
    abrechenbareMitglieder: true,
    auslastung: true,
    anwesenheit: true,
    kuendigungen: true,
    anwesenheitAuslastung: true,
    buchungsAuslastung: true,
    anwesenheitHistorie: true,
    auslastungHistorie: true,
    standortAuslastung: true,
    mitgliederHistorie: true,
    dashboardAustritteEintritte: true,
    mahnwesenDashboard: true,
    mahngebuhrenDashboard: true,
    anwesenheitDialog: true,
    auslastungDialog: true,
  });

  useEffect(() => {
    getAktiveAnmeldungenForCurrentMonth().then((response) => {
      setAktiveAnmeldungen(response);
      setLoadingStatus((prevState) => ({
        ...prevState,
        aktiveAnmeldungen: false,
      }));
    });
    getAbrechenbareMitgliederForCurrentMonth().then((response) => {
      setAbrechenbareMitglieder(response);
      setLoadingStatus((prevState) => ({
        ...prevState,
        abrechenbareMitglieder: false,
      }));
    });
    getBottomTenAuslastung().then((response) => {
      setBottomTenAuslastung(response.bottomTen);
      setAllAuslastung(response.all);
      setLoadingStatus((prevState) => ({
        ...prevState,
        auslastung: false,
        auslastungDialog: false,
      }));
    });
    getBottomTenAnwesenheit().then((response) => {
      setBottomTenAnwesenheit(response.bottomTen);
      setAllAnwesenheit(response.all);
      setLoadingStatus((prevState) => ({
        ...prevState,
        anwesenheit: false,
        anwesenheitDialog: false,
      }));
    });
    getKuendigungenForTwoMonths().then((response) => {
      setKuendigungen(response);
      setLoadingStatus((prevState) => ({
        ...prevState,
        kuendigungen: false,
      }));
    });
    getBuchungsAuslastung().then((response) => {
      setBuchungsAuslastung(response);
      setLoadingStatus((prevState) => ({
        ...prevState,
        buchungsAuslastung: false,
      }));
    });
    getAnwesenheitAuslastung().then((response) => {
      setAnwesenheitAuslastung(response);
      setLoadingStatus((prevState) => ({
        ...prevState,
        anwesenheitAuslastung: false,
      }));
    });
    getHistorischeWerte().then((response) => {
      setAnwesenheitHistorie(response.anwesenheiten);
      setAuslastungHistorie(response.buchungen);
      setMitgliederHistorie(response.abrechenbareMitglieder);
      setLoadingStatus((prevState) => ({
        ...prevState,
        anwesenheitHistorie: false,
      }));
      setLoadingStatus((prevState) => ({
        ...prevState,
        mitgliederHistorie: false,
      }));
    });
    getEintritteAustritteData().then((response) => {
      setDashboardAustritteEintritte({
        kundigungen: response.kundigungen,
        gruppeVerlassen: response.gruppeVerlassen,
        austritte: response.kundigungen.map(
          (kundigung, index) => kundigung + response.gruppeVerlassen[index]
        ),
        neueintritte: response.neueintritte,
        sportgruppeHinzufugen: response.sportgruppeHinzufugen,
        eintritte: response.neueintritte.map(
          (neueintritte, index) =>
            neueintritte + response.sportgruppeHinzufugen[index]
        ),
        labels: response.labels,
      });
      setLoadingStatus((prevState) => ({
        ...prevState,
        dashboardAustritteEintritte: false,
      }));
    });
    getStandortAuslastung().then((response) => {
      setStandortAuslastung(response);
      setLoadingStatus((prevState) => ({
        ...prevState,
        standortAuslastung: false,
      }));
    });
    getMahnwesenDashboardValues().then((response) => {
      setImportMahnungenCount(response.import);
      setExpiredMahnungenCount(response.expired);
      setLoadingStatus((prevState) => ({
        ...prevState,
        mahnwesenDashboard: false,
      }));
    });
    getMahngebuhrenDashboard().then((response) => {
      setMahngebuhren(response);
      setLoadingStatus((prevState) => ({
        ...prevState,
        mahngebuhrenDashboard: false,
      }));
    });
  }, []);

  return (
    <ThemeProvider theme={customTheme}>
      <AdminPortalHeader />
      <Box
        sx={{
          width: "90%",
          ml: "auto",
          mb: "30px",
          mr: isMobileView ? 0 : "auto",
        }}
      >
        <Grid
          container
          spacing={2}
          sx={{
            width: "100%",
            height: "100%",
            display: "grid",
            gridAutoRows: { xs: "minmax(180px, auto)", md: "240px" },
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              md: "repeat(3, minmax(0, 1fr))",
            },
            gap: 2,
          }}
        >
          <Box
            sx={{
              gridColumn: { xs: "1 / -1", md: "1" },
              gridRow: { md: "1" },
            }}
          >
            {/*-------Aktive Anmeldungen------- */}
            <DashboardCard
              title="Aktive Anmeldungen"
              gridRow="1"
              gridColumn="1"
              isLoading={loadingStatus.aktiveAnmeldungen}
              infoDialog="Aktive Anmeldungen = Probemitglieder + Mitglieder"
            >
              {aktiveAnmeldungen && (
                <DashboardCircleChart
                  total={aktiveAnmeldungen.totalMitglieder}
                  coloredValue={aktiveAnmeldungen.mitglieder}
                  grayValue={aktiveAnmeldungen.probemitgliederCurrentMonth}
                  coloredValueTitle="Mitgliedschaften"
                  grayValueTitle="Probemitglieder"
                />
              )}
            </DashboardCard>
          </Box>

          {/*-------Probemitglieder------- */}
          <Box
            sx={{
              gridColumn: { xs: "1 / -1", md: "2" },
              gridRow: { md: "1" },
            }}
          >
            <DashboardCard
              title="Probemitglieder"
              gridRow="1"
              gridColumn="2"
              alignItems="left"
              isLoading={loadingStatus.kuendigungen}
              infoDialog={
                "Probemitglieder = Belegungen mit Mitgliedsbeginn laufender Monat, Conversion Rate = (Probemitglieder - Widerrufe) / Probemitglieder"
              }
            >
              {kuendigungen && aktiveAnmeldungen && (
                <>
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Box>
                      <Typography variant="body1" sx={monthTitleStyle}>
                        {kuendigungen.currentMonthName}
                      </Typography>
                      <Typography variant="h4" sx={bigNumberStyle}>
                        {aktiveAnmeldungen.probemitgliederCurrentMonth}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body1" sx={monthTitleStyle}>
                        {kuendigungen.nextMonthName}
                      </Typography>
                      <Typography variant="h4" sx={bigNumberStyle}>
                        {aktiveAnmeldungen.probemitgliederNextMonth}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      paddingTop: "22px",
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <img
                      src={upArrow}
                      alt="info"
                      style={{
                        width: "12px",
                        height: "12px",
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        color: "#607783",
                        fontSize: "11px",
                        textAlign: "left",
                        paddingLeft: "6px",
                      }}
                    >
                      {`Conversion Rate ${kuendigungen.currentMonthName}: ${
                        aktiveAnmeldungen.conversionRate * 100
                      }%`}
                    </Typography>
                  </Box>
                </>
              )}
            </DashboardCard>
          </Box>

          {/*-------Auslastung | Standorten------- */}
          <Box
            sx={{
              gridColumn: { xs: "1 / -1", md: "3" },
              gridRow: { md: "1 / span 3" },
            }}
          >
            <DashboardCard
              title="Auslastung | Standorte"
              gridRow="1 / span 3"
              gridColumn="3"
              isLoading={loadingStatus.standortAuslastung}
              infoDialog="Auslastung = Abrechenbare Mitglieder / Kapazität (jeweils bezogen auf einen Standort)"
              customHeight="90%"
            >
              {standortAuslastung && (
                <LocationDashboardChart
                  labelMaxLength={isMobileView ? 15 : 25}
                  inputData={standortAuslastung}
                />
              )}
            </DashboardCard>
          </Box>

          {/*-------Abrechenbar------- */}
          <Box
            sx={{
              gridColumn: { xs: "1 / -1", md: "1" },
              gridRow: { md: "2" },
            }}
          >
            <DashboardCard
              title="Mitgliedschaften"
              gridRow="2"
              gridColumn="1"
              isLoading={loadingStatus.abrechenbareMitglieder}
              infoDialog="Anzahl der Belegungen (inkl. Zahler= true + nach erfolgter Probezeit)"
            >
              {abrechenbareMitglieder && (
                <DashboardCircleChart
                  total={abrechenbareMitglieder.totalAbrechenbareMitglieder}
                  coloredValue={abrechenbareMitglieder.gsAbrechenbareMitglieder}
                  grayValue={abrechenbareMitglieder.nonGsAbrechenbareMitglieder}
                  coloredValueTitle="Gesundheitssport"
                  grayValueTitle="Andere Abteilungen"
                />
              )}
            </DashboardCard>
          </Box>

          {/*-------Kündigungen------- */}
          <Box
            sx={{
              gridColumn: { xs: "1 / -1", md: "2" },
              gridRow: { md: "2" },
            }}
          >
            <DashboardCard
              title="Kündigungen"
              gridRow="2"
              gridColumn="2"
              alignItems="left"
              isLoading={loadingStatus.kuendigungen}
              infoDialog="Erfasst werden die Anzahl der Kündigungen, Widerrufe und Gruppen­austritte im laufenden Monat sowie die Anzahl der bereits für den kommenden Monat vorgemerkten Kündigungen."
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                {kuendigungen && (
                  <>
                    <Box>
                      <Typography variant="body1" sx={monthTitleStyle}>
                        {kuendigungen.currentMonthName}
                      </Typography>
                      <Typography variant="h4" sx={bigNumberStyle}>
                        {kuendigungen.currentMonthCount}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#607783",
                          textTransform: "uppercase",
                          fontWeight: "bold",
                          textAlign: "left",
                          fontSize: "12px",
                          paddingTop: "22px",
                          whiteSpace: "pre-line",
                        }}
                      >
                        {`Widerrufe: ${kuendigungen.widerrufCount}\n Gruppe verlassen: ${kuendigungen.gruppeVerlassenCount}`}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="body1" sx={monthTitleStyle}>
                        {kuendigungen.nextMonthName}
                      </Typography>
                      <Typography variant="h4" sx={bigNumberStyle}>
                        {kuendigungen.nextMonthCount}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </DashboardCard>
          </Box>

          {/*-------Bottom-10 | Auslastung------- */}
          <Box
            sx={{
              gridColumn: { xs: "1 / -1", md: "1" },
              gridRow: { md: "3 / span 2" },
            }}
          >
            <DashboardCard
              title="Bottom-10 | Buchungsauslastung"
              gridRow="3 / span 2"
              gridColumn="1"
              isLoading={loadingStatus.auslastung}
              onClickOnCard={() => {}}
              infoDialog="Auslastung in % = Aktive Anmeldungen / Kapazität"
            >
              <>
                <Box height={"30px"} width={"100%"} />
                {
                  <Box
                    width={"100%"}
                    height={"100%"}
                    onClick={() => {
                      if (allAuslastung) {
                        setIsAuslastungDialogOpen(true);
                      }
                    }}
                  >
                    {bottomTenAuslastung &&
                    Object.keys(bottomTenAuslastung).length > 0 ? (
                      <DashboardBarChart
                        auslastungData={bottomTenAuslastung}
                        coloredValueTitle={
                          "Aktive Anmeldungen in Kindersport + Probemitglieder"
                        }
                        grayValueTitle={"Kapazität"}
                        color={"rgb(225, 187, 65)"}
                      />
                    ) : (
                      <BottomNoData></BottomNoData>
                    )}
                  </Box>
                }
              </>
            </DashboardCard>
          </Box>
          {isAuslastungDialogOpen && (
            <DashboardDialog
              title={"Auslastung"}
              isOpen={true}
              onCloseDialog={() => setIsAuslastungDialogOpen(false)}
            >
              <>
                <DashboardDialogFilter
                  showDateFilter={false}
                  onFilterChange={async ({ standort }) => {
                    setLoadingStatus((prevState) => ({
                      ...prevState,
                      auslastungDialog: true,
                    }));

                    const newData = await getBottomTenAuslastung(standort);
                    setAllAuslastung(newData.all);

                    setLoadingStatus((prevState) => ({
                      ...prevState,
                      auslastungDialog: false,
                    }));
                  }}
                />
                {loadingStatus.auslastungDialog ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100%",
                      width: "100%",
                      textAlign: "center",
                    }}
                  >
                    <CircularProgress sx={{ color: "#C1C9CD" }} />
                  </Box>
                ) : allAuslastung && Object.keys(allAuslastung).length !== 0 ? (
                  <DashboardBarChart
                    auslastungData={allAuslastung}
                    coloredValueTitle={"Anmeldungen"}
                    grayValueTitle={"Kapazität"}
                    color={"rgb(225, 187, 65)"}
                  />
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      textAlign: "center",
                      p: 4,
                      border: "1px dashed #C1C9CD",
                      borderRadius: 2,
                      bgcolor: "#f9f9f9",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{ color: "#607783", fontWeight: 500, mb: 1 }}
                    >
                      Keine Kurse gefunden
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#90A4AE" }}>
                      Bitte passen Sie Ihre Filter an oder wählen Sie einen
                      anderen Zeitraum.
                    </Typography>
                  </Box>
                )}
              </>
            </DashboardDialog>
          )}

          {/*-------Bottom-10 | Anwesenheit------- */}
          <Box
            sx={{
              gridColumn: { xs: "1 / -1", md: "2" },
              gridRow: { md: "3 / span 2" },
            }}
          >
            <DashboardCard
              title="Bottom-10 | Anwesenheit"
              gridRow="3 / span 2"
              gridColumn="2"
              isLoading={loadingStatus.anwesenheit}
              onClickOnCard={() => {}}
              infoDialog="Anwesenheit in % = Teilnehmende (Durschnitt der letzten 8 Wochen) / Anmeldungen (Durschnitt der letzten 8 Wochen)"
            >
              <>
                <BottomFilter
                  startDate={bottomAnwesenheitFilter.startDate}
                  endDate={bottomAnwesenheitFilter.endDate}
                  onChange={({ startDate, endDate }) =>
                    setBottomAnwesenheitFilter({ startDate, endDate })
                  }
                />

                <Box
                  width={"100%"}
                  height={"100%"}
                  onClick={() => {
                    if (allAnwesenheit) {
                      setIsAnwesenheitDialogOpen(true);
                    }
                  }}
                >
                  {bottomTenAnwesenheit &&
                  Object.keys(bottomTenAnwesenheit).length > 0 ? (
                    <DashboardBarChart
                      anwesenheitData={bottomTenAnwesenheit}
                      coloredValueTitle={"Anwesende"}
                      grayValueTitle={"Anmeldungen"}
                    />
                  ) : (
                    <BottomNoData></BottomNoData>
                  )}
                </Box>
              </>
            </DashboardCard>
          </Box>

          {isAnwesenheitDialogOpen && (
            <DashboardDialog
              title={"Anwesenheit"}
              isOpen={true}
              onCloseDialog={() => setIsAnwesenheitDialogOpen(false)}
            >
              {
                <>
                  <DashboardDialogFilter
                    showDateFilter={true}
                    onFilterChange={async ({ date, standort }) => {
                      setLoadingStatus((prevState) => ({
                        ...prevState,
                        anwesenheitDialog: true,
                      }));

                      let startDate: Date | null = null;
                      let endDate: Date | null = null;

                      if (date) {
                        startDate = new Date(date);
                        startDate.setDate(1);
                        endDate = new Date(date);
                        endDate.setMonth(endDate.getMonth() + 1);
                        endDate.setDate(0);
                      }

                      const newData = await getBottomTenAnwesenheit(
                        startDate || undefined,
                        endDate || undefined,
                        standort
                      );
                      setAllAnwesenheit(newData.all);

                      setLoadingStatus((prevState) => ({
                        ...prevState,
                        anwesenheitDialog: false,
                      }));
                    }}
                  />
                  {loadingStatus.anwesenheitDialog ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                        width: "100%",
                        textAlign: "center",
                      }}
                    >
                      <CircularProgress sx={{ color: "#C1C9CD" }} />
                    </Box>
                  ) : allAnwesenheit &&
                    Object.keys(allAnwesenheit).length !== 0 ? (
                    <DashboardBarChart
                      anwesenheitData={allAnwesenheit}
                      coloredValueTitle={"Teilnehmende"}
                      grayValueTitle={"Anmeldungen"}
                    />
                  ) : (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        height: "100%",
                        textAlign: "center",
                        p: 4,
                        border: "1px dashed #C1C9CD",
                        borderRadius: 2,
                        bgcolor: "#f9f9f9",
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{ color: "#607783", fontWeight: 500, mb: 1 }}
                      >
                        Keine Kurse gefunden
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#90A4AE" }}>
                        Bitte passen Sie Ihre Filter an oder wählen Sie einen
                        anderen Zeitraum.
                      </Typography>
                    </Box>
                  )}
                </>
              }
            </DashboardDialog>
          )}

          {/*-------Buchungsauslastung | Kindersport------- */}
          <Box
            sx={{
              gridColumn: { xs: "1 / -1", md: "1" },
              gridRow: { md: "5" },
            }}
          >
            <DashboardCard
              title="Buchungsauslastung | Kindersport"
              gridRow="4"
              gridColumn="3"
              isLoading={loadingStatus.buchungsAuslastung}
              infoDialog="Buchungsauslastung (%) = Summe aller aktiven Anmeldungen ÷ Summe der maximalen Kapazitäten aller aktiven Kurse."
            >
              {buchungsAuslastung && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    gap: "3px",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <DashboardCircleChart
                      total={`${buchungsAuslastung.auslastung.toFixed(0)}%`}
                      coloredValue={buchungsAuslastung.aktiveAnmeldungen}
                      coloredValueTitle="Alle Aktiven Anmeldungen in Kindersport + Probemitglieder"
                      grayValue={buchungsAuslastung.kapa}
                      coloredValueBgColor={"rgb(225, 187, 65)"}
                      isPercentage={true}
                    />
                  </Box>
                  <ChartsLegend
                    usedValueTitle={`${buchungsAuslastung.aktiveAnmeldungen} Alle Aktiven Anmeldungen in Kindersport + Probemitglieder`}
                    usedValueBgColor={"rgb(225, 187, 65)"}
                    capValueTitle={`${buchungsAuslastung.kapa} Kapazität`}
                  />
                </Box>
              )}
            </DashboardCard>
          </Box>

          {/*-------Anwesenheitsauslastung | Kindersport------- */}
          <Box
            sx={{
              gridColumn: { xs: "1 / -1", md: "2" },
              gridRow: { md: "5" },
            }}
          >
            <DashboardCard
              title="Anwesenheitsauslastung | Kindersport"
              gridRow="4"
              gridColumn="1"
              isLoading={loadingStatus.anwesenheitAuslastung}
              infoDialog="Standarddaten beziehen sich auf die Check Out Complete Kurse des letzen Monats. Zeitspanne kann durch den Filter in Bottom-10 | Anwesenheit verändert werden. Anwesenheitsquote (%) = Summe aller durschnittlich anwesenden Teilnehmer ÷ Summe aller durschnittlich eingeschriebenen Teilnehmer"
            >
              {anwesenheitAuslastung && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "100%",
                    gap: "3px",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <DashboardCircleChart
                      total={`${anwesenheitAuslastung.auslastung.toFixed(0)}%`}
                      coloredValue={anwesenheitAuslastung.checkIns}
                      grayValue={anwesenheitAuslastung.teilnehmer}
                      isPercentage={true}
                    />
                  </Box>
                  <ChartsLegend
                    usedValueTitle={`${anwesenheitAuslastung.checkIns} Anwesende`}
                    capValueTitle={`${anwesenheitAuslastung.teilnehmer} Anmeldungen`}
                  />
                </Box>
              )}
            </DashboardCard>
          </Box>

          <Box
            sx={{
              gridColumn: {
                xs: "1 / -1",
                md:
                  mahngebuhren &&
                  Object.keys(mahngebuhren).length > LIMIT_FOR_MAHNGEBUHR
                    ? "1 / span 2"
                    : "1",
              },
              gridRow: { md: "6" },
            }}
          >
            <DashboardCard
              title={"Mahngebühren"}
              gridRow="5"
              gridColumn={
                mahngebuhren &&
                Object.keys(mahngebuhren).length > LIMIT_FOR_MAHNGEBUHR
                  ? "1 / span 2"
                  : "1"
              }
              isLoading={loadingStatus.mahngebuhrenDashboard}
              infoDialog="Mahngebühren aller Mahnungen mit Status Aktiv oder Import der letzten sechs Monate“"
            >
              {mahngebuhren && <MahnwesenBarChart data={mahngebuhren} />}
            </DashboardCard>
          </Box>

          <Box
            sx={{
              gridColumn: {
                xs: "1 / -1",
                md:
                  mahngebuhren &&
                  Object.keys(mahngebuhren).length > LIMIT_FOR_MAHNGEBUHR
                    ? "3"
                    : "2",
              },
              gridRow: { md: "6" },
            }}
          >
            <DashboardCard
              title="Mahnvorgänge"
              gridRow="5"
              gridColumn={
                mahngebuhren &&
                Object.keys(mahngebuhren).length > LIMIT_FOR_MAHNGEBUHR
                  ? "3"
                  : "2"
              }
              alignItems="left"
              isLoading={loadingStatus.kuendigungen}
              infoDialog={
                "Import = Mahnungen mit dem Status IMPORT | Abgelaufene Frist = Aktive Mahnungen, deren Mahnfrist überschritten ist"
              }
            >
              {importMahnungenCount !== undefined &&
                expiredMahnungenCount !== undefined && (
                  <>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Box>
                        <Typography variant="body1" sx={monthTitleStyle}>
                          {"Import"}
                        </Typography>
                        <Typography variant="h4" sx={bigNumberStyle}>
                          {importMahnungenCount}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body1" sx={monthTitleStyle}>
                          {"Abgelaufene Frist"}
                        </Typography>
                        <Typography variant="h4" sx={bigNumberStyle}>
                          {expiredMahnungenCount}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}
            </DashboardCard>
          </Box>

          {/*-------Anwesenheit Historie------- */}
          <Box
            sx={{
              gridColumn: { xs: "1 / -1", md: "1 / span 3" },
              gridRow: { md: "7" },
            }}
          >
            <DashboardCard
              title={"Anwesenheits- und Buchungsauslastung gesamt"}
              gridRow="6"
              gridColumn="1 / span 3"
              isLoading={
                loadingStatus.anwesenheitHistorie &&
                loadingStatus.auslastungHistorie
              }
              infoDialog="Die historischen Monatswerte der letzten 12 Monate für die Anwesenheit werden angezeigt. Der Wert ergibt sich aus der durchschnittlichen Kapazität des Kurses geteilt durch die durchschnittliche Teilnehmerzahl."
            >
              <DashboardChart
                isPercentage={true}
                max={100}
                datasets={[
                  {
                    label: "Anwesenheitsauslastung",
                    data: anwesenheitHistorie,
                    borderColor: "rgba(68, 174, 176, 1)",
                    backgroundColor: "rgba(68, 174, 176, 0.5)",
                  },
                  {
                    label: "Buchungsauslastung",
                    data: auslastungHistorie,
                    borderColor: "rgb(225, 187, 65)",
                    backgroundColor: "rgba(225, 187, 65, 0.5)",
                  },
                ]}
              />
            </DashboardCard>
          </Box>

          {/*-------Anmeldungen Historie------- */}
          <Box
            sx={{
              gridColumn: { xs: "1 / -1", md: "1 / span 3" },
              gridRow: { md: "8" },
            }}
          >
            <DashboardCard
              title={"abrechenbar Gesamt"}
              gridRow="7"
              gridColumn="1 / span 3"
              isLoading={loadingStatus.mitgliederHistorie}
              infoDialog="Die historischen Monatswerte der letzten 12 Monate für die Anmeldungen werden angezeigt. Anmeldungen stehen hierbei für die Menge an Anmeldungen von abrechenbaren Mtigliedern."
            >
              <DashboardChart
                max={roundUpToNearest500(Math.max(...mitgliederHistorie))}
                min={
                  mitgliederHistorie.length > 0
                    ? Math.floor(
                        (Math.min(...mitgliederHistorie) * 0.9) / 100
                      ) * 100
                    : undefined
                }
                datasets={[
                  {
                    label: "Zahler",
                    data: mitgliederHistorie,
                    borderColor: "rgb(193, 201, 205)",
                    backgroundColor: "rgba(193, 201, 205, 0.5)",
                  },
                ]}
              />
            </DashboardCard>
          </Box>

          {/*-------Austritte | Eintritte Gesamt------- */}
          <Box
            sx={{
              gridColumn: { xs: "1 / -1", md: "1 / span 3" },
              gridRow: { md: "9" },
            }}
          >
            <DashboardCard
              title={"Austritte | Eintritte Gesamt"}
              gridRow="8"
              gridColumn="1 / span 3"
              isLoading={loadingStatus.dashboardAustritteEintritte}
              infoDialog="Historische Monatswerte der letzten 2 Jahre für Austritte und Eintritte werden angezeigt."
            >
              {dashboardAustritteEintritte && (
                <DashboardChart
                  max={
                    dashboardAustritteEintritte
                      ? roundUpToNearest50(
                          Math.max(
                            ...dashboardAustritteEintritte.austritte,
                            ...dashboardAustritteEintritte.eintritte
                          )
                        )
                      : 300
                  }
                  customLabels={dashboardAustritteEintritte.labels}
                  datasets={[
                    {
                      label: "Austritte",
                      data: dashboardAustritteEintritte
                        ? dashboardAustritteEintritte.austritte
                        : [],
                      borderColor: "rgb(250, 105, 105)",
                      backgroundColor: "rgba(250, 105, 105, 0.5)",
                    },
                    {
                      label: "Eintritte",
                      data: dashboardAustritteEintritte
                        ? dashboardAustritteEintritte.eintritte
                        : [],
                      borderColor: "rgba(68, 174, 176, 1)",
                      backgroundColor: "rgba(68, 174, 176, 0.5)",
                    },
                  ]}
                  customTooltipCallbacks={{
                    Eintritte: (tooltipItem: any, dataIndex: number) => {
                      if (!dashboardAustritteEintritte) return [];
                      return [
                        `Eintritte: ${dashboardAustritteEintritte.eintritte[dataIndex]}`,
                        `Neueintritte: ${dashboardAustritteEintritte.neueintritte[dataIndex]}`,
                        `Sportgruppe hinzugefügt: ${dashboardAustritteEintritte.sportgruppeHinzufugen[dataIndex]}`,
                      ];
                    },
                    Austritte: (tooltipItem: any, dataIndex: number) => {
                      if (!dashboardAustritteEintritte) return [];
                      return [
                        `Austritte: ${dashboardAustritteEintritte.austritte[dataIndex]}`,
                        `Kündigungen: ${dashboardAustritteEintritte.kundigungen[dataIndex]}`,
                        `Gruppe Verlassen: ${dashboardAustritteEintritte.gruppeVerlassen[dataIndex]}`,
                      ];
                    },
                  }}
                />
              )}
            </DashboardCard>
          </Box>
        </Grid>
      </Box>
    </ThemeProvider>
  );
};

export default AdminDashboardPage;
